import React from 'react'

const projects = [
  {
    title: 'DevDetective',
    desc: 'A GitHub profile finder that fetches public developer data, repositories, followers, and location in a clean searchable interface.',
    impact: 'Shows API handling, async state, and clean UI feedback.',
    tech: ['HTML5', 'CSS3', 'JavaScript', 'GitHub API'],
    github: 'https://github.com/omkarkardel',
    live: 'https://dev-detective-ok.netlify.app/',
  },
  {
    title: 'CampusHub',
    desc: 'A centralized campus event platform where students can discover events, view details, track schedules, and register from one place.',
    impact: 'Built around a real student workflow: discover, decide, register.',
    tech: ['MongoDB', 'Express.js', 'React.js', 'Node.js', 'JavaScript', 'HTML5', 'CSS3'],
    github: 'https://github.com/omkarkardel/CampusHub.git',
    live: 'https://campus-hub-web.vercel.app',
  },
  {
    title: 'ITNotesHub',
    desc: 'A MERN education resource hub for IT engineering students with organized notes, books, handwritten notes, and PYQs.',
    impact: 'Solves content discovery with structured academic resources.',
    tech: ['MongoDB', 'Express.js', 'React.js', 'Node.js', 'JavaScript', 'HTML5', 'CSS3'],
    github: 'https://github.com/omkarkardel/ITNotesHub.git',
    live: 'https://it-noteshub.vercel.app',
  },
  {
    title: 'Diet Recommendation System',
    desc: 'A personalized diet recommendation interface that turns user inputs into customized nutrition suggestions.',
    impact: 'Highlights form-driven UX, conditional logic, and Tailwind UI.',
    tech: ['React.js', 'HTML5', 'Tailwind CSS'],
    github: 'https://github.com/omkarkardel/Diet-Recommendation-System.git',
    live: 'https://diet-recommendation-systems.netlify.app',
  },
]

const accents = [
  'from-blue-500/25 via-blue-500/10 to-transparent',
  'from-violet-500/25 via-violet-500/10 to-transparent',
  'from-emerald-500/25 via-emerald-500/10 to-transparent',
  'from-amber-500/25 via-amber-500/10 to-transparent',
]

const accentBorders = [
  'group-hover:border-blue-500/40',
  'group-hover:border-violet-500/40',
  'group-hover:border-emerald-500/40',
  'group-hover:border-amber-500/40',
]

const GitHubIcon = () => (
  <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
    <path d="M12 0C5.37 0 0 5.37 0 12c0 5.31 3.435 9.795 8.205 11.385.6.105.825-.255.825-.57 0-.285-.015-1.23-.015-2.235-3.015.555-3.795-.735-4.035-1.395-.135-.345-.72-1.395-1.23-1.695-.42-.225-1.02-.78-.015-.795.945-.015 1.62.87 1.845 1.23 1.08 1.815 2.805 1.305 3.495.99.105-.78.42-1.305.765-1.605-2.67-.3-5.46-1.335-5.46-5.925 0-1.305.465-2.385 1.23-3.225-.12-.3-.54-1.53.12-3.18 0 0 1.005-.315 3.3 1.23.96-.27 1.98-.405 3-.405s2.04.135 3 .405c2.295-1.56 3.3-1.23 3.3-1.23.66 1.65.24 2.88.12 3.18.765.84 1.23 1.905 1.23 3.225 0 4.605-2.805 5.625-5.475 5.925.435.375.81 1.095.81 2.22 0 1.605-.015 2.895-.015 3.3 0 .315.225.69.825.57A12.02 12.02 0 0024 12c0-6.63-5.37-12-12-12z" />
  </svg>
)

const ExternalIcon = () => (
  <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2" aria-hidden="true">
    <path strokeLinecap="round" strokeLinejoin="round" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
  </svg>
)

const ProjectCard = ({ project, index }) => {
  const visibleTech = project.tech.slice(0, 4)
  const extraCount = project.tech.length - visibleTech.length

  return (
    <article
      className={`reveal reveal-delay-${(index % 2) + 1} card-lift group flex h-full flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm dark:border-slate-700 dark:bg-slate-800/80 ${accentBorders[index]}`}
    >
      <div className={`relative h-28 bg-gradient-to-br ${accents[index]} dark:from-opacity-30`}>
        <div
          className="absolute inset-0 opacity-[0.35] dark:opacity-20"
          style={{
            backgroundImage:
              'linear-gradient(rgba(37,99,235,0.08) 1px, transparent 1px), linear-gradient(90deg, rgba(37,99,235,0.08) 1px, transparent 1px)',
            backgroundSize: '20px 20px',
          }}
        />
        <div className="relative flex h-full items-end justify-between px-5 pb-4">
          <span className="font-mono text-xs font-medium text-slate-500 dark:text-slate-400">
            0{index + 1}
          </span>
          <span className="rounded-full border border-slate-200/80 bg-white/80 px-2.5 py-1 text-xs font-medium text-slate-600 dark:border-slate-600 dark:bg-slate-900/80 dark:text-slate-300">
            Project
          </span>
        </div>
      </div>

      <div className="flex flex-1 flex-col p-5">
        <h3 className="text-lg font-semibold text-slate-900 dark:text-white">{project.title}</h3>
        <p className="mt-2 line-clamp-3 text-sm leading-relaxed text-slate-600 dark:text-slate-400">
          {project.desc}
        </p>

        <div className="mt-4 rounded-xl border border-slate-200 bg-slate-50 p-3 dark:border-slate-700 dark:bg-slate-900/50">
          <p className="text-xs font-semibold uppercase tracking-wider text-primary">Impact</p>
          <p className="mt-1 text-sm leading-relaxed text-slate-700 dark:text-slate-300">{project.impact}</p>
        </div>

        <div className="mt-4 flex flex-1 flex-wrap content-start gap-1.5">
          {visibleTech.map((t) => (
            <span
              key={t}
              className="rounded-md bg-slate-100 px-2 py-0.5 text-xs font-medium text-slate-600 dark:bg-slate-900 dark:text-slate-300"
            >
              {t}
            </span>
          ))}
          {extraCount > 0 && (
            <span className="rounded-md bg-slate-100 px-2 py-0.5 text-xs font-medium text-slate-500 dark:bg-slate-900 dark:text-slate-400">
              +{extraCount}
            </span>
          )}
        </div>

        <div className="mt-5 flex gap-2 border-t border-slate-100 pt-4 dark:border-slate-700">
          <a
            href={project.github}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex flex-1 items-center justify-center gap-1.5 rounded-lg border border-slate-200 px-3 py-2 text-sm font-medium text-slate-700 transition-colors hover:border-slate-300 hover:bg-slate-50 dark:border-slate-600 dark:text-slate-200 dark:hover:border-slate-500 dark:hover:bg-slate-900"
          >
            <GitHubIcon />
            GitHub
          </a>
          <a
            href={project.live}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex flex-1 items-center justify-center gap-1.5 rounded-lg bg-primary px-3 py-2 text-sm font-medium text-white transition-colors hover:bg-primary-dark"
          >
            <ExternalIcon />
            Live
          </a>
        </div>
      </div>
    </article>
  )
}

const Projects = () => {
  return (
    <section id="projects" className="scroll-mt-16 bg-slate-50 py-10 dark:bg-slate-950 md:py-12">
      <div className="mx-auto max-w-6xl px-4">
        <div className="reveal">
          <h2 className="text-center text-3xl font-semibold tracking-tight text-slate-900 dark:text-white">
            My Projects
          </h2>
          <div className="mx-auto mt-3 h-1 w-12 rounded bg-primary" />
          <p className="mx-auto mt-4 max-w-2xl text-center text-slate-600 dark:text-slate-400">
            Projects chosen to show product thinking, frontend polish, backend structure, and deployment practice.
          </p>
        </div>

        <div className="mt-8 grid gap-6 sm:grid-cols-2">
          {projects.map((p, i) => (
            <ProjectCard key={p.title} project={p} index={i} />
          ))}
        </div>
      </div>
    </section>
  )
}

export default Projects
